/**
 * routesTrelloMap.ts — Catálogo de Rotas, Cards do Trello e Histórico de Discussões
 */

import trelloData from './trello-knowledge.json';

export interface TrelloImage {
  alt: string;
  url: string;
}

export interface TrelloAttachment {
  id: string;
  name: string;
  mimeType: string;
  url: string;
  previewUrl: string;
  thumbnailUrl: string;
  isImage: boolean;
  isVideo: boolean;
  date: string;
}

export interface TrelloComment {
  id: string;
  author: string;
  username: string;
  avatarUrl: string | null;
  initials: string;
  date: string;
  text: string;
  images?: TrelloImage[];
}

export interface TrelloChecklist {
  id: string;
  name: string;
  items: { id: string; name: string; state: string }[];
}

export interface TrelloCardDetail {
  id: string;
  name: string;
  desc: string;
  rawDesc?: string;
  listName: string;
  status: string;
  url: string;
  dateLastActivity: string;
  labels: { name: string; color: string }[];
  targetRoutes?: string[];
  routePattern: string;
  commentsCount: number;
  comments: TrelloComment[];
  checklists: TrelloChecklist[];
  attachments?: TrelloAttachment[];
  descImages?: TrelloImage[];
}

export interface RouteTrelloMeta {
  routePattern: string;
  routeName: string;
  trelloCard: string;
  trelloCardId?: string;
  trelloUrl?: string;
  sprint?: string;
  status: 'CONCLUIDO' | 'HOMOLOGAÇÃO' | 'DESENVOLVIMENTO' | 'DEMANDA' | 'RETORNO' | 'PENDÊNCIAS';
  lastAuditDate: string;
  lastDecision: string;
  testedFeatures: string[];
  acBlocks?: string[];
  relatedTrelloCards?: TrelloCardDetail[];
}

export const ALL_TRELLO_CARDS: TrelloCardDetail[] = (trelloData.cards || []) as unknown as TrelloCardDetail[];

export const ROUTES_TRELLO_MAP: RouteTrelloMeta[] = [
  {
    routePattern: '/admin/comunicados',
    routeName: 'Gestão de Comunicados da Escola',
    trelloCard: 'Otimização Mobile de Comunicados e Wizard',
    trelloCardId: 'card_com_01',
    trelloUrl: 'https://trello.com/b/6a60c4a919e2a7bb16b3ea5a',
    sprint: 'Sprint Core v5.63',
    status: 'CONCLUIDO',
    lastAuditDate: '2026-08-18 04:03',
    lastDecision: 'KPIs em 5 colunas estáticas (44px) sem scroll horizontal. DataToolbar mobile com gaveta de filtros. Modal Wizard fullscreen no mobile sem preview lateral nos passos 1 e 2.',
    testedFeatures: [
      'KPIs estáticos 5 colunas (1 linha)',
      'DataToolbar retrátil e drawer de filtros',
      'RowActionMenu 3 pontos para ações',
      'Modal Wizard Fullscreen Mobile',
      'Preservação 100% layout Desktop'
    ],
    acBlocks: ['com-kpi-grid', 'com-wizard-mobile', 'com-datatoolbar']
  },
  {
    routePattern: '/gestao/escolas',
    routeName: 'Gestão Geral de Escolas',
    trelloCard: 'Cadastro Escola & Redesign Cards 2x2',
    trelloCardId: '6a7109cc198174677dca14d9',
    trelloUrl: 'https://trello.com/c/kbI3dwoM',
    sprint: 'Sprint Core v5.63',
    status: 'CONCLUIDO',
    lastAuditDate: '2026-08-18 03:50',
    lastDecision: 'Abertura direta em modo Cards no mobile. Cards compactos 2x2 com métricas essenciais e menu de 3 pontos no cabeçalho. Correção de multi-tenant em primeiro acesso.',
    testedFeatures: [
      'Detecção automática de largura < 768px para modo Cards',
      'Card 2x2 compacto com contagens e status',
      'RowActionMenu para edição, bloqueio e exclusão',
      'Isolamento de dados por escola_id'
    ],
    acBlocks: ['escolas-card-grid', 'escolas-datatoolbar']
  },
  {
    routePattern: '/admin/chamadas-gestao',
    routeName: 'Gestão e Fechamento de Chamadas',
    trelloCard: 'Painel Professor & Fechamento de Chamadas',
    trelloCardId: '6a7f2f5a60ed43c7b9de0f96',
    trelloUrl: 'https://trello.com/c/8z0fIztf',
    sprint: 'Sprint Core v5.62',
    status: 'CONCLUIDO',
    lastAuditDate: '2026-08-18 01:20',
    lastDecision: 'Filtros rápidos por turno e turma, sincronização com tags RFID, conferência de limite de tolerância de horário e cálculo automático de frequência do MEC.',
    testedFeatures: [
      'Lançamento em lote de presença/falta/justificativa',
      'Painel de divergência facial/RFID',
      'Exportação de diário de classe',
      'Validação de limite de prazo com timezone escolar'
    ],
    acBlocks: ['chamadas-gestao-hub']
  },
  {
    routePattern: '/admin/equipe',
    routeName: 'Gestão de Usuários e Equipe Escolar',
    trelloCard: 'Cadastro Usuário & Recuperação de Acesso',
    trelloCardId: '6a6ccbc5a31b9779cce807e5',
    trelloUrl: 'https://trello.com/c/iGTId7KT',
    sprint: 'Sprint Core v5.61',
    status: 'CONCLUIDO',
    lastAuditDate: '2026-08-18 00:20',
    lastDecision: 'DataToolbar padrão integrado com busca retrátil, badges de cargo dinâmicos, correção de persistência de notificações e alteração segura de senhas.',
    testedFeatures: [
      'DataToolbar unificado',
      'Convite de novo membro com QR Code / Magic Link',
      'Controle granular de permissões',
      'Recuperar acesso com MFA'
    ],
    acBlocks: ['equipe-datatoolbar', 'equipe-rbac']
  },
  {
    routePattern: '/admin/professores',
    routeName: 'Quadro Docente e Matérias',
    trelloCard: 'Quadro Docente & Vinculação de Turmas',
    trelloCardId: '6a7f336e7b1a14062087ee6c',
    trelloUrl: 'https://trello.com/c/cLL1Ank4',
    sprint: 'Sprint Core v5.61',
    status: 'CONCLUIDO',
    lastAuditDate: '2026-08-17 23:50',
    lastDecision: 'DataToolbar com filtros rápidos de turno e especialidade. Sincronização de avisos para app móvel.',
    testedFeatures: ['DataToolbar', 'Vínculo de Turma/Matéria', 'Status de Acesso'],
    acBlocks: ['professores-grid']
  },
  {
    routePattern: '/admin/alunos',
    routeName: 'Ficha Cadastral e Matrícula de Alunos',
    trelloCard: 'Cadastro Modular de Alunos e Tags RFID',
    trelloCardId: 'card_aln_06',
    trelloUrl: 'https://trello.com/b/6a60c4a919e2a7bb16b3ea5a',
    sprint: 'Sprint Core v5.62',
    status: 'CONCLUIDO',
    lastAuditDate: '2026-08-18 02:40',
    lastDecision: 'Refinamentos no formulário de alunos e vínculo de responsáveis com validação estrita de CPF.',
    testedFeatures: ['Formulário com tabs', 'Associação RFID', 'Vínculo Responsável'],
    acBlocks: ['alunos-form', 'alunos-contract']
  },
  {
    routePattern: '/admin/turmas',
    routeName: 'Gestão de Turmas e Séries',
    trelloCard: 'Cadastro Turma: Validação e Consulta',
    trelloCardId: '6a710ec4ed7e5bf7a4e78d24',
    trelloUrl: 'https://trello.com/c/giLYIf2v',
    sprint: 'Sprint Core v5.61',
    status: 'CONCLUIDO',
    lastAuditDate: '2026-08-17 22:30',
    lastDecision: 'DataToolbar universal com contagem de alunos por turno e visualização rápida.',
    testedFeatures: ['DataToolbar', 'Distribuição de Alunos', 'Horários'],
    acBlocks: ['turmas-grid']
  },
  {
    routePattern: '/admin/turnos',
    routeName: 'Cadastro e Horários de Turnos',
    trelloCard: 'Cadastro de Turno: Rolagem e Horários',
    trelloCardId: '6a710baaa23792364ece0a81',
    trelloUrl: 'https://trello.com/c/H4O2Ctp6',
    sprint: 'Sprint Core v5.61',
    status: 'CONCLUIDO',
    lastAuditDate: '2026-08-17 21:00',
    lastDecision: 'Inclusão de scroll vertical responsivo e validação de grades de horário.',
    testedFeatures: ['Scroll de turnos', 'Horários por dia da semana', 'Validação de conflitos'],
    acBlocks: ['turnos-template']
  },
  {
    routePattern: '/chat',
    routeName: 'Chat Institucional e Mensageria',
    trelloCard: 'Chat Interno: Correção de Cabeçalho e Mensagens',
    trelloCardId: '6a722e55261c685970635fd9',
    trelloUrl: 'https://trello.com/c/K3P68tmT',
    sprint: 'Sprint Core v5.62',
    status: 'CONCLUIDO',
    lastAuditDate: '2026-08-18 01:45',
    lastDecision: 'Unificação do Chat Widget flutuante com suporte a cabeçalho clicável, WebSocket realtime e upload de anexos.',
    testedFeatures: ['WebSocket Realtime', 'Drawer Flutuante e Rota Fixa', 'Upload e Status de Leitura'],
    acBlocks: ['chat-widget-global', 'chat-core']
  },
  {
    routePattern: '/treinamento',
    routeName: 'Ajuda, Guias e Treinamento Interativo',
    trelloCard: 'Ajuda e Treinamento: Roteamento de Tours por Módulo',
    trelloCardId: '6a7347bbe0e1c6a34d3ddf0b',
    trelloUrl: 'https://trello.com/c/MF4Kx6IA',
    sprint: 'Sprint Core v5.62',
    status: 'CONCLUIDO',
    lastAuditDate: '2026-08-17 20:00',
    lastDecision: 'Mapeamento dinâmico de tours contextuais para o módulo ativo em vez do tour genérico inicial.',
    testedFeatures: ['Tours Contextuais', 'Vídeos Interativos', 'Progresso de Aprendizado'],
    acBlocks: ['training-tour-runner']
  }
];

export function getTrelloCardsForRoute(pathname: string): TrelloCardDetail[] {
  const norm = pathname.split('?')[0].split('#')[0].replace(/\/+$/, '') || '/';

  return ALL_TRELLO_CARDS.filter((c) => {
    // 1. Verifica se a rota está explicitamente nas targetRoutes extraídas do Trello
    const trs = c.targetRoutes || [c.routePattern];
    if (Array.isArray(trs) && trs.length > 0) {
      if (trs.includes(norm)) return true;
      if (trs.some((r: string) => r && (norm === r || (norm.startsWith(r) && r !== '/' && r !== '/dashboard' && r !== '/master/dashboard')))) {
        return true;
      }
    }

    // 2. Se a rota for /master/dashboard, apenas cards com menção estrita à dashboard master
    if (norm === '/master/dashboard') {
      return c.desc.includes('/master/dashboard') || (trs && trs.includes('/master/dashboard'));
    }

    if (norm === '/admin/dashboard' || norm === '/dashboard') {
      return c.desc.includes('/admin/dashboard') || (trs && trs.includes('/admin/dashboard'));
    }

    return false;
  });
}

export function getRouteTrelloMeta(pathname: string): RouteTrelloMeta {
  const norm = pathname.replace(/\/+$/, '') || '/';
  const match = ROUTES_TRELLO_MAP.find((m) => {
    if (m.routePattern === norm) return true;
    return norm.startsWith(m.routePattern) && m.routePattern !== '/';
  });

  const relatedCards = getTrelloCardsForRoute(norm);

  if (match) {
    return {
      ...match,
      relatedTrelloCards: relatedCards,
    };
  }

  // Se houver cards correlacionados do Trello para essa rota específica
  if (relatedCards.length > 0) {
    const primaryCard = relatedCards[0];
    const totalComments = relatedCards.reduce((acc, c) => acc + c.commentsCount, 0);

    return {
      routePattern: norm,
      routeName: norm.replace(/\//g, ' ').trim().toUpperCase(),
      trelloCard: `${primaryCard.name} (${relatedCards.length} cards • ${totalComments} 💬)`,
      trelloCardId: primaryCard.id,
      trelloUrl: primaryCard.url,
      sprint: 'Sprint Core',
      status: (primaryCard.status as any) || 'HOMOLOGAÇÃO',
      lastAuditDate: new Date(primaryCard.dateLastActivity).toLocaleDateString('pt-BR'),
      lastDecision: primaryCard.desc || 'Módulo acompanhado e auditado via Quadro Oficial de Desenvolvimento do Trello.',
      testedFeatures: primaryCard.checklists.flatMap(chk => chk.items.map(it => it.name)).length > 0
        ? primaryCard.checklists.flatMap(chk => chk.items.map(it => it.name))
        : ['Navegação Segura', 'Autenticação JWT', 'Isolamento de Tenant (Escola ID)'],
      acBlocks: ['master-core'],
      relatedTrelloCards: relatedCards,
    };
  }

  return {
    routePattern: norm,
    routeName: norm.replace(/\//g, ' ').trim().toUpperCase() || 'PAINEL PRINCIPAL',
    trelloCard: 'Sprint Core — Aluno Conectado',
    trelloCardId: 'card_generic',
    trelloUrl: 'https://trello.com/b/6a60c4a919e2a7bb16b3ea5a',
    sprint: 'Sprint Core v5.63',
    status: 'CONCLUIDO',
    lastAuditDate: '2026-08-18 04:03',
    lastDecision: 'Módulo integrado à plataforma oficial sob governança do ecossistema Aluno Conectado.',
    testedFeatures: ['Navegação Segura', 'Autenticação JWT', 'Isolamento de Tenant (Escola ID)'],
    acBlocks: ['base-layout'],
    relatedTrelloCards: relatedCards,
  };
}
